import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Database, Globe2, Receipt, Search } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Badge, DataTable, EmptyState, formatBytes, formatDate, Input, PageHeader, Pagination, Panel, Select, StatusBadge } from "@hostdock/ui";
import { api } from "../../api";
import { RequirePermission } from "../../lib/guard";
import { useListParams } from "../../lib/list-params";
import { SslBadge } from "./shared";
import { ResourceState } from "../customer-subscriptions";

const PAGE_SIZE = 50;

function SearchBar({ value, onSearch, placeholder, children }: { value: string; onSearch(v: string): void; placeholder: string; children?: React.ReactNode }) {
  const [v, setV] = useState(value);
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
      <form className="relative min-w-52 flex-1" onSubmit={(e) => { e.preventDefault(); onSearch(v.trim()); }}>
        <Search size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-subtle" />
        <Input value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} className="pl-8" aria-label={placeholder} />
      </form>
      {children}
    </div>
  );
}

export function DatabasesPage() {
  return <RequirePermission anyOf={["WEBSITE_VIEW", "DATABASE_MANAGE"]}><DatabasesList /></RequirePermission>;
}

function DatabasesList() {
  const navigate = useNavigate();
  const { values, page, set } = useListParams(["search"] as const);
  const q = useQuery({ queryKey: ["databases", values, page], queryFn: () => api.databases.list({ ...values, page, pageSize: PAGE_SIZE }), placeholderData: keepPreviousData, refetchInterval: 15_000 });
  return (
    <>
      <PageHeader title="Databases" description="Tenant databases on node-level MariaDB, PostgreSQL and MongoDB." />
      <Panel flush>
        <SearchBar value={values.search} onSearch={(s) => set({ search: s })} placeholder="Database name" />
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(d) => d.id}
          onRowClick={(d) => d.websiteId && navigate(`/hosting/websites/${d.websiteId}?tab=databases`)}
          empty={<EmptyState icon={Database} title="No databases" />}
          columns={[
            { key: "n", header: "Database", cell: (d) => <span className="font-mono text-[13px]">{d.name}</span> },
            { key: "e", header: "Engine", cell: (d) => <Badge>{d.engine === "MARIADB" ? "MariaDB" : d.engine === "POSTGRESQL" ? "PostgreSQL" : "MongoDB"}</Badge> },
            { key: "s", header: "Status", cell: (d) => <StatusBadge status={d.status === "ACTIVE" ? "ACTIVE" : d.status} /> },
            { key: "size", header: "Size", cell: (d) => <span className="tabular-nums text-muted">{d.sizeBytes != null ? formatBytes(d.sizeBytes) : "—"}</span> },
            { key: "c", header: "Created", cell: (d) => <span className="text-muted">{formatDate(d.createdAt)}</span>, hideOnMobile: true },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={(p) => set({ page: String(p) })} />}
      </Panel>
    </>
  );
}

export function DomainsPage() {
  return <RequirePermission anyOf={["WEBSITE_VIEW"]}><DomainsList /></RequirePermission>;
}

function DomainsList() {
  const navigate = useNavigate();
  const { values, page, set } = useListParams(["search", "status"] as const);
  const q = useQuery({ queryKey: ["domains", values, page], queryFn: () => api.domains.list({ ...values, page, pageSize: PAGE_SIZE }), placeholderData: keepPreviousData, refetchInterval: 15_000 });
  return (
    <>
      <PageHeader title="Domains" description="All domains served by HostDock, their DNS verification and certificate status." />
      <Panel flush>
        <SearchBar value={values.search} onSearch={(s) => set({ search: s })} placeholder="Domain">
          <Select value={values.status} onChange={(e) => set({ status: e.target.value })} className="w-44" aria-label="DNS status">
            <option value="">All</option><option value="VERIFIED">Verified</option><option value="PENDING_VERIFICATION">Pending DNS</option>
          </Select>
        </SearchBar>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(d) => d.id}
          onRowClick={(d) => navigate(`/hosting/websites/${d.websiteId}?tab=domains`)}
          empty={<EmptyState icon={Globe2} title="No domains" />}
          columns={[
            { key: "n", header: "Domain", cell: (d) => <span className="font-medium">{d.name}</span> },
            { key: "k", header: "Type", cell: (d) => <Badge>{d.kind.charAt(0) + d.kind.slice(1).toLowerCase()}</Badge> },
            { key: "s", header: "DNS", cell: (d) => d.status === "VERIFIED" ? <Badge tone="success">Verified</Badge> : <Badge tone="warning">Pending</Badge> },
            { key: "ssl", header: "Certificate", cell: (d) => d.certificate ? <SslBadge status={d.certificate.status} /> : <span className="text-subtle">—</span> },
            { key: "exp", header: "Expires", cell: (d) => <span className="text-muted">{d.certificate?.notAfter ? formatDate(d.certificate.notAfter) : "—"}</span>, hideOnMobile: true },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={(p) => set({ page: String(p) })} />}
      </Panel>
    </>
  );
}

export function SubscriptionsPage() {
  return <RequirePermission anyOf={["CUSTOMER_VIEW"]}><SubscriptionsList /></RequirePermission>;
}

function SubscriptionsList() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["subscriptions-all"], queryFn: api.subscriptions.all });
  return (
    <>
      <PageHeader title="Subscriptions" description="Plans assigned to customers. Billing integrations create these automatically in a later phase." />
      <Panel flush>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(s) => s.id}
          onRowClick={(s) => navigate(`/customers/${s.customerId}`)}
          empty={<EmptyState icon={Receipt} title="No subscriptions" description="Open a customer and assign a plan." />}
          columns={[
            { key: "c", header: "Customer", cell: (s) => <span className="font-medium">{s.customerName}</span> },
            { key: "p", header: "Plan", cell: (s) => s.plan.name },
            { key: "st", header: "Status", cell: (s) => <StatusBadge status={s.status === "CANCELLED" ? "CLOSED" : s.status} /> },
            { key: "u", header: "Usage", cell: (s) => <span className="tabular-nums text-muted">{s.usage.websites} sites · {s.usage.databases} DBs</span> },
            { key: "res", header: "Resources", cell: (s) => <ResourceState resourceLimit={s.resourceLimit} bandwidthExceeded={s.bandwidthExceeded} /> },
            { key: "r", header: "Billing ref", cell: (s) => <span className="font-mono text-[12px] text-muted">{s.externalRef ?? "—"}</span>, hideOnMobile: true },
            { key: "d", header: "Since", cell: (s) => <span className="text-muted">{formatDate(s.createdAt)}</span>, hideOnMobile: true },
          ]}
        />
      </Panel>
    </>
  );
}
